import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { api, errMsg, useFetch } from "../lib/api";
import { money, imgUrl, fmtDate, STATUS_LABELS } from "../lib/shop";
import { AdminTitle } from "./AdminLayout";
import { PayBadge } from "./Overview";
import { destinationText } from "../components/site/DeliveryFields";

function OrderDetail({ number, onChanged }) {
  const { data: o, setData } = useFetch(`/admin/orders/${number}`);
  const [status, setStatus] = useState("");
  const [tracking, setTracking] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (o) { setStatus(o.status); setTracking(o.tracking_number || ""); } }, [o]);
  if (!o) return <p className="text-zinc-400">Loading...</p>;
  const c = o.customer;
  const update = async () => {
    try { const r = await api.put(`/admin/orders/${number}/status`, { status, note, tracking_number: tracking }); setData(r.data); setNote(""); toast.success("Order updated"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const verify = async () => {
    try { const r = await api.post(`/admin/orders/${number}/verify-payment`); toast.success(`Payment status: ${r.data.payment_status}`); const d = await api.get(`/admin/orders/${number}`); setData(d.data); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6 text-sm" data-testid="admin-order-detail">
      <div className="flex flex-wrap gap-2 items-center"><PayBadge s={o.payment_status} /><span className="text-zinc-400">{STATUS_LABELS[o.status]} · {fmtDate(o.created_at)}</span></div>
      {o.stock_conflicts?.length > 0 && <p className="text-red-300 bg-red-500/10 p-3 rounded-lg">Stock conflict — could not reserve: {o.stock_conflicts.join(", ")}. Contact the customer.</p>}
      <div className="card-dark p-4 space-y-1">
        <p className="label">Customer</p>
        <p className="font-bold" data-testid="admin-order-customer">{c.first_name} {c.last_name}</p>
        <p><a href={`mailto:${c.email}`} className="underline">{c.email}</a> · <a href={`tel:${c.phone}`} className="underline">{c.phone}</a></p>
        <p className="text-zinc-300" data-testid="admin-order-destination"><b className="text-gold">{o.delivery_zone?.name}</b> · {destinationText(c, o.delivery_zone?.method)}</p>
        {c.instructions && <p className="text-zinc-400">Instructions: {c.instructions}</p>}
      </div>
      <div className="card-dark p-4 space-y-3">
        <p className="label">Items</p>
        {o.items.map((l) => (
          <div key={l.product_id + (l.colour || "") + l.size} className="flex gap-3"><img src={imgUrl(l.image)} alt="" className="w-12 h-12 rounded object-cover" />
            <div className="flex-1"><p className="font-semibold">{l.name}</p><p className="text-xs text-zinc-400">{l.colour ? <>Colour <b className="text-white" data-testid="admin-order-item-colour">{l.colour}</b> · </> : null}Size {l.size} · Qty {l.qty} · {money(l.unit_price)}{l.sku ? <> · SKU <b className="text-white" data-testid="admin-order-item-sku">{l.sku}</b></> : null}</p></div><b>{money(l.line_total)}</b></div>
        ))}
        <div className="border-t border-white/10 pt-3 space-y-1">
          <p className="flex justify-between"><span>Subtotal</span><span>{money(o.subtotal)}</span></p>
          <p className="flex justify-between"><span>Delivery ({o.delivery_zone?.name})</span><span>{money(o.delivery_fee)}</span></p>
          {o.discount > 0 && <p className="flex justify-between"><span>Discount ({o.discount_code})</span><span>-{money(o.discount)}</span></p>}
          <p className="flex justify-between font-extrabold text-base"><span>Total</span><span className="text-gold">{money(o.total)}</span></p>
        </div>
      </div>
      <div className="card-dark p-4 space-y-1">
        <p className="label">Payment</p>
        <p>Provider: {o.payment.provider}{o.payment.provider === "demo" && <span className="text-amber-300"> (MOCKED demo payment)</span>}</p>
        <p data-testid="admin-order-payment-ref">Reference: {o.payment.reference || "—"} · Paylink: {o.payment.paylink_id || "—"}</p>
        {o.payment_status === "pending" && o.payment.provider === "ikhokha" && <button onClick={verify} className="chip mt-2" data-testid="admin-verify-payment">Re-check payment with iKhokha</button>}
      </div>
      <div className="card-dark p-4 space-y-3">
        <p className="label">Update status</p>
        <select className="field" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-order-status-select">
          {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input className="field" placeholder="Courier tracking number (optional)" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="admin-order-tracking-input" />
        <input className="field" placeholder="Internal note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-order-note-input" />
        <button onClick={update} className="btn-gold w-full" data-testid="admin-order-status-save">Save status</button>
      </div>
      <div><p className="label">History</p>{[...o.status_history].reverse().map((h, i) => <p key={i} className="text-xs text-zinc-400 py-1">{fmtDate(h.at)} — <b className="text-white">{STATUS_LABELS[h.status]}</b> {h.note && `· ${h.note}`}</p>)}</div>
    </div>
  );
}

export default function Orders() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ status: "", payment_status: "", q: "" });
  const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
  const { data, reload } = useFetch(`/admin/orders?${qs}`);
  const open = params.get("open");
  const setOpen = (n) => setParams(n ? { open: n } : {});
  return (
    <div>
      <AdminTitle title="Orders" />
      <div className="flex flex-wrap gap-2 mb-4">
        <input className="field max-w-xs" placeholder="Search order, name, email, phone" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="admin-orders-search" />
        <select className="field !w-auto" value={filters.payment_status} onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })} data-testid="admin-orders-payment-filter">
          <option value="">All payments</option><option value="paid">Paid</option><option value="pending">Pending</option><option value="failed">Failed</option><option value="cancelled">Cancelled</option>
        </select>
        <select className="field !w-auto" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="admin-orders-status-filter">
          <option value="">All statuses</option>{Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div className="card-dark overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]" data-testid="admin-orders-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-zinc-400 border-b border-white/10">
            {["Order", "Date", "Customer", "Products", "Value", "Delivery", "Payment", "Status"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
          <tbody>
            {(data || []).map((o) => (
              <tr key={o.id} onClick={() => setOpen(o.order_number)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="p-3 font-bold text-gold">{o.order_number}</td>
                <td className="p-3 text-zinc-400 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                <td className="p-3">{o.customer.first_name} {o.customer.last_name}<span className="block text-xs text-zinc-500">{o.customer.phone}</span></td>
                <td className="p-3 text-xs">{o.items.map((i) => `${i.name} (${[i.colour, i.size, i.sku].filter(Boolean).join(", ")}) ×${i.qty}`).join(", ")}</td>
                <td className="p-3 font-bold">{money(o.total)}</td>
                <td className="p-3">{money(o.delivery_fee)}</td>
                <td className="p-3"><PayBadge s={o.payment_status} /></td>
                <td className="p-3">{STATUS_LABELS[o.status]}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.length === 0 && <p className="p-6 text-zinc-500">No orders found.</p>}
      </div>
      <Sheet open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <SheetContent className="bg-[#0a0a0c] border-white/10 w-full sm:max-w-xl overflow-y-auto">
          <SheetTitle className="font-display text-4xl text-gold mb-4">{open}</SheetTitle>
          {open && <OrderDetail number={open} onChanged={reload} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
